import { DecimalType } from './../pages/admin/campaign-data-analysis/constants';
import { SxProps, Theme } from '@mui/material';
import { TOKEN_KEY } from 'common/constants';
import { toast } from 'react-toastify';
import numeral from 'plugins/numeral';

// style create component
type CSSProperties = Record<string, SxProps<Theme>>;

export const createStyleSheet = <Styles extends CSSProperties>(
    styles: Styles
): Styles => {
    return styles;
};

export const removeStorage = (key: string): void => {
    try {
        localStorage.removeItem(key);
    } catch (err) {
        console.error('Remove storage error:', err);
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- set item to local storage any type
export const setItemLocalStorage = (key: string, state: any): void => {
    // Chấp nhận bất kỳ loại dữ liệu nào
    try {
        const serializedState = JSON.stringify(state);
        localStorage.setItem(key, serializedState);
    } catch (err) {
        console.error('Save storage error:', err);
        // Ignore write errors.
    }
};

export const getItemLocalStorage = (key: string) => {
    try {
        const serializedState = localStorage.getItem(key);
        if (serializedState === null) {
            return undefined;
        }
        return JSON.parse(serializedState);
    } catch (err) {
        console.error('Load storage error:', err);
        return undefined;
    }
};

export const getItemSessionStorage = (key: string) => {
    try {
        const serializedState = sessionStorage.getItem(key);
        if (serializedState === null) {
            return undefined;
        }
        return JSON.parse(serializedState);
    } catch (err) {
        console.error('Load session storage error:', err);
        return undefined;
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- set item to local storage any type
export const setSessionStorage = (key: string, state: any): void => {
    try {
        const serializedState = JSON.stringify(state);
        sessionStorage.setItem(key, serializedState);
    } catch (err) {
        console.error('Save session storage error:', err);
    }
};

export const removeSessionStorage = (key: string): void => {
    try {
        sessionStorage.removeItem(key);
    } catch (err) {
        console.error('Remove session storage error:', err);
    }
};

export const encode = (value: string): string => {
    return encodeURIComponent(value);
};

export const getToken = () => {
    return getItemLocalStorage(TOKEN_KEY) || getItemSessionStorage(TOKEN_KEY);
};

export const clearAllDataBrowser = () => {
    localStorage.clear();
    sessionStorage.clear();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- error type any
export const handleError = (error: any) => {
    if (error instanceof Error) {
        toast.error(error.message);
    }
};

numeral.locales['vi'] = {
    abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
    },
    delimiters: {
        thousands: '.',
        decimal: ',',
    },
    ordinal: () => '',
    currency: {
        symbol: '₫',
    },
};

numeral.locale('vi');

export const formatCurrency = (number: number | string): string => {
    numeral.locale('vi');
    return numeral(number).format('0,0');
};

export const objectToFormData = (
    obj: any, // eslint-disable-line
    form?: FormData,
    namespace?: string
): FormData => {
    const formData = form || new FormData();

    for (const property in obj) {
        if (
            !Object.prototype.hasOwnProperty.call(obj, property) ||
            obj[property] === undefined ||
            obj[property] === null
        ) {
            continue;
        }

        const formKey = namespace ? `${namespace}[${property}]` : property;

        if (obj[property] instanceof Date) {
            formData.append(formKey, obj[property].toISOString());
        } else if (
            typeof obj[property] === 'object' &&
            !(obj[property] instanceof File)
        ) {
            objectToFormData(obj[property], formData, formKey);
        } else {
            formData.append(formKey, obj[property]);
        }
    }

    return formData;
};

export const applyTokenToUrl = (url: string, staff_id_param = '') => {
    const searchParams = new URLSearchParams(window.location.search);
    const token = searchParams.get('token') || '';
    const surveyCode = searchParams.get('surveyCode') || '';
    const code = searchParams.get('code') || '';
    const staff_id =
        searchParams.get('staff_id') ||
        searchParams.get('staffId') ||
        staff_id_param;
    const isSecretSurvey = searchParams.get('isSecretSurvey') || '';
    if (url.includes('?')) {
        return `${url}&token=${token}&surveyCode=${surveyCode}&code=${code}&staff_id=${staff_id}${isSecretSurvey ? `&isSecretSurvey=${isSecretSurvey}` : ''}`;
    }
    return `${url}?token=${token}&surveyCode=${surveyCode}&code=${code}&staff_id=${staff_id}${isSecretSurvey ? `&isSecretSurvey=${isSecretSurvey}` : ''}`;
};

export const formatNumberDecimal = (value: number, decimalType: string) => {
    if (decimalType === DecimalType.INTEGER) {
        return Number.parseFloat(value.toString()).toFixed(0);
    }
    if (decimalType === DecimalType.ONE_DECIMAL) {
        return Number.parseFloat(value.toString()).toFixed(1);
    }
    if (decimalType === DecimalType.TWO_DECIMALS) {
        return Number.parseFloat(value.toString()).toFixed(2);
    }
    return Number.parseFloat(value.toString()).toFixed(0);
};

export const generateColors = (length: number) =>
    Array.from({ length }, (_, i) => `hsl(${(i * 360) / length}, 70%, 50%)`);

export const getValueFromPercent = (
    percent: number,
    total: number,
    digits = 0
) => {
    if (total === 0) return 0;

    return parseFloat(((percent / 100) * total).toFixed(digits));
};

/** Sinh màu bù (complementary) bằng cách xoay hue 180° — dùng làm màu thứ 2 tương phản với màu chính. */
export const getComplementaryColor = (hex: string): string => {
    try {
        const h = hex.startsWith('#') ? hex : `#${hex}`;
        const r = parseInt(h.slice(1, 3), 16) / 255;
        const g = parseInt(h.slice(3, 5), 16) / 255;
        const b = parseInt(h.slice(5, 7), 16) / 255;
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        const l = (max + min) / 2;
        let hue = 0;
        let s = 0;
        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case r:
                    hue = ((g - b) / d + (g < b ? 6 : 0)) / 6;
                    break;
                case g:
                    hue = ((b - r) / d + 2) / 6;
                    break;
                case b:
                    hue = ((r - g) / d + 4) / 6;
                    break;
            }
        }
        const compH = ((hue * 360 + 180) % 360) / 360;
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        const hue2rgb = (t: number) => {
            if (t < 0) t += 1;
            if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        const toHex = (v: number) =>
            Math.round(v * 255)
                .toString(16)
                .padStart(2, '0');
        return `#${toHex(hue2rgb(compH + 1 / 3))}${toHex(hue2rgb(compH))}${toHex(hue2rgb(compH - 1 / 3))}`;
    } catch {
        return '#FF7916';
    }
};
